import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useTranslation } from 'react-i18next';
import { AuthenticatedImage } from '@/components/ui';

const ClockIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m4-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const UserIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

function statusBadge(status: string, t: (k: string) => string) {
  const map: Record<string, { label: string; cls: string }> = {
    PREPARED:     { label: t('recettes.statusPrepared'),     cls: 'bg-green-100 text-green-800' },
    VALID:        { label: t('recettes.statusValid'),        cls: 'bg-green-100 text-green-800' },
    EXPIRING_SOON:{ label: t('recettes.statusExpiringSoon'), cls: 'bg-amber-100 text-amber-800' },
    EXPIRED:      { label: t('recettes.statusExpired'),      cls: 'bg-red-100 text-red-800' },
    DESTROYED:    { label: t('recettes.statusDestroyed'),    cls: 'bg-slate-100 text-slate-600' },
    STORED:       { label: t('recettes.statusStored'),       cls: 'bg-blue-100 text-blue-800' },
  };
  const entry = map[status] || { label: status, cls: 'bg-slate-100 text-slate-600' };
  return (
    <span className={`inline-block text-sm font-semibold px-3 py-1 rounded-full ${entry.cls}`}>
      {entry.label}
    </span>
  );
}

export default function DLCScanPage() {
  const navigate = useNavigate();
  const { batchId } = useParams<{ batchId: string }>();
  const apiClient = useApiClient();
  const { t, i18n } = useTranslation();

  const normalizedBatchId = useMemo(() => (batchId || '').trim(), [batchId]);

  const { data: batch, isLoading, isError } = useQuery({
    queryKey: ['dlc-scan-batch', normalizedBatchId],
    queryFn: () => apiClient.getBatchByBatchId(normalizedBatchId),
    enabled: normalizedBatchId.length > 0,
  });

  const batchName = batch ? (batch.name || batch.internalPreparation?.name || '-') : '';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex flex-col items-center justify-start sm:justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md my-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">{t('recettes.scanTitle')}</h2>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-3 py-1.5 text-sm font-medium text-orange-100 bg-white/20 rounded-lg hover:bg-white/30 transition"
            >
              {t('common.close')}
            </button>
          </div>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500" />
            </div>
          ) : isError || !batch ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center">
              <p className="text-red-700 font-medium">{t('recettes.scanNotFound')}</p>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Photo */}
              {batch.photoUrl && (
                <div className="rounded-xl overflow-hidden h-44 bg-slate-100">
                  <AuthenticatedImage src={batch.photoUrl} alt={batchName} className="w-full h-full object-cover" />
                </div>
              )}

              {/* Name + status */}
              <div className="text-center">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{batchName}</h3>
                {statusBadge(batch.status, t)}
              </div>

              {/* Start / End dates */}
              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center gap-3 bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ClockIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">{t('recettes.startLabel')}</p>
                    <p className="text-base font-bold text-blue-900">
                      {new Date(batch.productionDateTime).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  </div>
                </div>

                <div className={`flex items-center gap-3 rounded-xl p-4 border ${batch.status === 'EXPIRED' ? 'bg-red-50 border-red-200' : batch.status === 'EXPIRING_SOON' ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-100'}`}>
                  <div className={`p-2 rounded-lg ${batch.status === 'EXPIRED' ? 'bg-red-100' : batch.status === 'EXPIRING_SOON' ? 'bg-amber-100' : 'bg-green-100'}`}>
                    <ClockIcon className={`w-5 h-5 ${batch.status === 'EXPIRED' ? 'text-red-600' : batch.status === 'EXPIRING_SOON' ? 'text-amber-600' : 'text-green-600'}`} />
                  </div>
                  <div>
                    <p className={`text-xs font-semibold uppercase tracking-wide ${batch.status === 'EXPIRED' ? 'text-red-600' : batch.status === 'EXPIRING_SOON' ? 'text-amber-600' : 'text-green-600'}`}>{t('recettes.endLabel')}</p>
                    <p className={`text-base font-bold ${batch.status === 'EXPIRED' ? 'text-red-900' : batch.status === 'EXPIRING_SOON' ? 'text-amber-900' : 'text-green-900'}`}>
                      {new Date(batch.dlc).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Quantity + created by */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-1">{t('recettes.quantity')}</p>
                  <p className="text-lg font-bold text-slate-900">{batch.quantity} {batch.unit}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <div className="flex items-center gap-1 mb-1">
                    <UserIcon className="w-3.5 h-3.5 text-slate-400" />
                    <p className="text-xs text-slate-500 uppercase font-semibold">{t('recettes.createdBy')}</p>
                  </div>
                  <p className="text-sm font-bold text-slate-900 truncate">
                    {batch.createdBy?.displayName || batch.createdBy?.email || '-'}
                  </p>
                </div>
              </div>

              {/* Batch ID */}
              <div className="text-center">
                <p className="text-xs text-slate-400 font-mono">{batch.batchId}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
